// Rematch: the panels from design/project/App17b-RematchOffer, App18b-RematchSent and App18c-RematchPassed. The result
// screens' REMATCH button offers the last opponent the same terms for 60 seconds; the opponent sees the offer over
// their result screen (or the lobby) and accepts or passes. Both tapping REMATCH starts the battle at once.
import { useEffect, useState, type ReactNode } from "react";
import { usdt } from "@degenduel/shared";
import { ApiError } from "../api.js";
import { useGame, useTicker, type Battle } from "../game.js";
import { go, setBackOverride } from "../router.js";
import { play } from "../sound.js";
import { arenaLabel, lastRound, otherSide, stakeLabel, type Challenge } from "../challenge.js";
import { leadColour } from "./Rivalry.js";
import { CHALLENGE_SIDE } from "./App21-ChallengeFriend.js";

const UN = { fontFamily: "Unbounded, sans-serif", fontWeight: "900" } as const;
const MONO = { fontFamily: "'JetBrains Mono', monospace", fontWeight: "800" } as const;
const BTN_P = { height: "56px", border: "2px solid #0B0E14", borderRadius: "16px", background: "#CCFF00", color: "#0B0E14", ...UN, fontSize: "15px", boxShadow: "0 5px 0 #5E7500, 0 5px 0 2px #0B0E14", cursor: "pointer" } as const;
const BTN_S = { height: "48px", border: "2px solid #30363D", borderRadius: "14px", background: "transparent", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: "700", fontSize: "14px", cursor: "pointer" } as const;

const initials = (name: string) => name.replace(/^@/, "").slice(0, 2).toUpperCase() || "??";
const mmss = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, "0")}`;

function Avatar({ name, bg, dim }: { name: string; bg: string; dim?: boolean }) {
  return <div style={{ flex: "none", width: "52px", height: "52px", borderRadius: "16px", background: bg, border: "3px solid #0B0E14", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", ...UN, fontSize: "16px", color: "#0B0E14", opacity: dim ? 0.5 : 1 }}>{initials(name)}</div>;
}

function Terms({ arena, durationSec, stake, you }: { arena: string; durationSec: number; stake: string; you: "long" | "short" }) {
  const cell = { display: "flex", flexDirection: "column", alignItems: "center", gap: "3px", flex: "1", minWidth: "0" } as const;
  const lbl = { ...UN, fontSize: "8px", letterSpacing: "0.08em", color: "#8B949E" } as const;
  const val = { ...MONO, fontSize: "13px", whiteSpace: "nowrap" } as const;
  return (
    <div style={{ marginTop: "14px", display: "flex", padding: "10px 4px", borderRadius: "14px", background: "#0B0E14", border: "2px solid #30363D" }}>
      <div style={cell}><span style={lbl}>ARENA</span><span style={val}>{arenaLabel(arena)}</span></div>
      <div style={cell}><span style={lbl}>LENGTH</span><span style={val}>{durationSec === 60 ? "1:00" : "3:00"}</span></div>
      <div style={cell}><span style={lbl}>STAKE</span><span style={val}>{stakeLabel(stake)} USDT</span></div>
      <div style={cell}><span style={lbl}>YOU TAKE</span>{you === "short" ? <span style={{ ...val, color: "#FF3366" }}>▼ SHORT</span> : <span style={{ ...val, color: "#CCFF00" }}>▲ LONG</span>}</div>
    </div>
  );
}

/** "HEAD TO HEAD · YOU 3 – 2 THEM" in one line, inside a rematch panel. */
function Score({ mine, theirs }: { mine: number; theirs: number }) {
  return (
    <div style={{ marginTop: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", padding: "8px", borderRadius: "12px", background: "rgba(204,255,0,0.06)" }}>
      <span style={{ ...UN, fontSize: "9px", letterSpacing: "0.12em", color: "#8B949E" }}>HEAD TO HEAD</span>
      <span style={{ ...MONO, fontSize: "16px", color: leadColour(mine, theirs) }}>YOU {mine}</span>
      <span style={{ ...MONO, fontSize: "16px", color: "#4B535E" }}>–</span>
      <span style={{ ...MONO, fontSize: "16px" }}>{theirs} THEM</span>
    </div>
  );
}

function Bar({ left, of }: { left: number; of: number }) {
  return (
    <div style={{ marginTop: "12px", height: "6px", borderRadius: "3px", background: "#30363D", overflow: "hidden" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, (left / of) * 100))}%`, height: "100%", background: "#CCFF00", transition: "width 0.5s linear" }}></div>
    </div>
  );
}

function Sheet({ edge, children }: { edge: string; children: ReactNode }) {
  return (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: "0", zIndex: 40, background: "rgba(11,14,20,0.74)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div style={{ boxSizing: "border-box", width: "100%", maxWidth: "480px", alignSelf: "center", padding: "18px 20px calc(var(--tg-safe-area-inset-bottom, 34px) + var(--tg-content-safe-area-inset-bottom, 0px) + 16px)", maxHeight: "calc(100% - var(--tg-safe-area-inset-top, 47px) - var(--tg-content-safe-area-inset-top, 47px))", overflowY: "auto", borderRadius: "26px 26px 0 0", background: "#161B22", borderTop: `3px solid ${edge}`, boxShadow: "0 -10px 40px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
        <div style={{ alignSelf: "center", width: "40px", height: "4px", borderRadius: "2px", background: "#30363D", marginBottom: "14px" }}></div>
        {children}
      </div>
    </div>
  );
}

function Head({ name, bg, tag, tagColor, sub, clock, dim }: { name: string; bg: string; tag?: string; tagColor?: string; sub: string; clock?: string; dim?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <Avatar name={name} bg={bg} dim={dim} />
      <div style={{ flex: "1", minWidth: "0", display: "flex", flexDirection: "column", gap: "2px" }}>
        {tag && <span style={{ ...UN, fontSize: "9px", letterSpacing: "0.1em", color: tagColor }}>{tag}</span>}
        <span style={{ ...UN, fontSize: "17px", lineHeight: "22px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name.replace(/^@/, "").toUpperCase()}</span>
        <span style={{ fontSize: "13px", lineHeight: "18px", color: "#8B949E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
      </div>
      {clock && <span style={{ ...MONO, fontSize: "20px", color: "#CCFF00", flex: "none" }}>{clock}</span>}
    </div>
  );
}

/** An offer waiting for this player: on the result screens and the lobby. */
export function RematchOffer({ offer }: { offer: Challenge }) {
  const { me, now, acceptChallenge, declineRematch } = useGame();
  useTicker(500);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = Math.max(0, offer.expiresAt - now());
  if (left === 0) return null;
  const stake = stakeLabel(offer.stake);
  const canAfford = !!me && usdt(me.balance.available) >= usdt(offer.stake);
  const accept = async () => {
    if (busy) return;
    play("tap");
    setBusy(true);
    setError(null);
    try {
      await acceptChallenge(offer.id);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "network";
      setError(code === "low_balance" ? `You need ${stake} USDT for this rematch.` : code === "network" ? "No connection. Try again." : code === "already_queued" ? "Cancel your search first." : "This rematch is no longer open.");
      setBusy(false);
    }
  };
  const r = offer.rivalry;
  const revenge = r?.lastWinner === "target";
  return (
    <Sheet edge="#FF3366">
      <Head name={offer.creator.name} bg="#FF3366" tag={revenge ? "REVENGE" : "REMATCH"} tagColor="#FF3366" sub={r ? `${lastRound(offer)} · ${revenge ? "wants revenge" : "wants another go"}` : `wants another go for ${stake} USDT`} clock={mmss(left)} />
      {r && <Score mine={r.targetWins} theirs={r.creatorWins} />}
      <Bar left={left} of={offer.openMs || 60_000} />
      <Terms arena={offer.arena} durationSec={offer.durationSec} stake={offer.stake} you={otherSide(offer.side)} />
      {(error || !canAfford) && <span role="alert" style={{ marginTop: "12px", textAlign: "center", fontSize: "13px", color: "#FF5C85" }}>{error ?? `Not enough balance: you need ${stake} USDT.`}</span>}
      <button style={{ ...BTN_P, marginTop: "16px", opacity: canAfford ? 1 : 0.45 }} disabled={busy || !canAfford} onClick={() => void accept()}>{busy ? "LOCKING..." : `ACCEPT · LOCK ${stake} USDT`}</button>
      <button style={{ ...BTN_S, marginTop: "10px" }} disabled={busy} onClick={() => void declineRematch(offer.id)}>No thanks</button>
    </Sheet>
  );
}

/** Why the last rematch didn't happen, for the panel that says so. */
type Passed = { why: "expired" | "moved_on" | "passed" | "closed" | "busy" | "low" | "cooldown" | "my_low" | "old" | "network" | "other"; retryAt?: number };

/**
 * The REMATCH button's action and the panels that follow it: waiting for the opponent, and "no rematch this time".
 * A rematch offer for this battle waiting for this player shows instead.
 */
export function useRematch(battle: Battle | null, oppName: string) {
  const { me, now, challenge, incoming, rematch, cancelChallenge, topUp, select, mySide, checkChallenges } = useGame();
  useTicker(500);
  const [busy, setBusy] = useState(false);
  const [passed, setPassed] = useState<Passed | null>(null);
  const mine = challenge?.kind === "rematch" && challenge.fromBattle === battle?.id ? challenge : null;
  const offer = incoming.find((c) => c.fromBattle === battle?.id) ?? null;
  const waiting = mine?.state === "open";

  // The answer comes over the live socket; a check every few seconds covers a socket that dropped.
  useEffect(() => {
    if (!battle) return;
    const t = setInterval(checkChallenges, 5_000);
    return () => clearInterval(t);
  }, [battle?.id]);

  // Our offer ended without a battle: say how.
  useEffect(() => {
    if (!mine || mine.state === "open" || mine.state === "accepted") return;
    if (mine.state === "cancelled" && !mine.closeReason) return; // we called it off ourselves
    setPassed({ why: mine.state === "expired" ? "expired" : mine.closeReason === "moved_on" ? "moved_on" : mine.state === "declined" ? "passed" : "closed" });
  }, [mine?.state]);

  // Back while waiting takes the offer back first.
  useEffect(() => {
    if (!waiting) return;
    setBackOverride(() => void cancelChallenge().catch(() => {}).then(() => go("App09", true)));
    return () => setBackOverride(null);
  }, [waiting]);

  const stake = battle?.stake ?? "0";
  const canAfford = !!me && usdt(me.balance.available) >= usdt(stake);
  const tap = async () => {
    if (!battle || busy) return;
    if (!canAfford) return void topUp();
    play("tap");
    setBusy(true);
    setPassed(null);
    try {
      await rematch(battle.id);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "network";
      const retryAt = e instanceof ApiError && typeof e.detail.retryAt === "number" ? e.detail.retryAt : undefined;
      const why: Passed["why"] =
        code === "opponent_busy" ? "busy" : code === "opponent_low_balance" ? "low" : code === "cooldown" ? "cooldown" :
        code === "low_balance" ? "my_low" : code === "no_rematch" ? "old" : code === "network" ? "network" : "other";
      setPassed({ why, retryAt });
    } finally {
      setBusy(false);
    }
  };

  const withLink = () => {
    if (!battle) return;
    const s = stakeLabel(battle.stake);
    select({ arena: battle.arena, stake: s, custom: !["2", "10", "50", "250"].includes(s), durationSec: battle.durationSec === 60 ? 60 : 180 });
    if (mySide) sessionStorage.setItem(CHALLENGE_SIDE, mySide);
    setPassed(null);
    go("App21");
  };

  let sheet: JSX.Element | null = null;
  if (offer) sheet = <RematchOffer offer={offer} />;
  else if (mine && waiting) {
    const left = Math.max(0, mine.expiresAt - now());
    sheet = (
      <Sheet edge="#CCFF00">
        <Head name={oppName} bg="#8A7CFF" tag="REMATCH SENT" tagColor="#CCFF00" sub={mine.notified ? "Left the app · the bot told them" : mine.openMs > 60_000 ? "Left the app · open 5 minutes" : "Waiting for them to accept"} clock={mmss(left)} />
        {mine.rivalry && <Score mine={mine.rivalry.creatorWins} theirs={mine.rivalry.targetWins} />}
        <Bar left={left} of={mine.openMs || 60_000} />
        <Terms arena={mine.arena} durationSec={mine.durationSec} stake={mine.stake} you={mine.side} />
        <span style={{ marginTop: "14px", textAlign: "center", fontSize: "12px", lineHeight: "17px", color: "#8B949E" }}>Your {stakeLabel(mine.stake)} USDT is locked. If they accept, the battle starts at once.</span>
        <button style={{ ...BTN_S, marginTop: "14px", color: "#FF5C85", borderColor: "rgba(255,92,133,0.4)" }} onClick={() => void cancelChallenge().catch(() => {})}>Cancel · get {stakeLabel(mine.stake)} USDT back</button>
      </Sheet>
    );
  } else if (passed) {
    const wait = passed.retryAt ? Math.max(0, passed.retryAt - now()) : 0;
    const refunded = !!mine && mine.state !== "open" && mine.state !== "accepted";
    sheet = (
      <Sheet edge="#30363D">
        <Head name={oppName} bg="#8A7CFF" dim sub={{
          expired: `${oppName} didn't answer`, moved_on: `${oppName} moved on`, busy: `${oppName} moved on`, passed: `${oppName} passed`,
          cooldown: `${oppName} passed`, low: `${oppName} can't cover ${stakeLabel(stake)} USDT`, closed: "The arena closed",
          my_low: "Not enough balance", old: "This battle is too old for a rematch", network: "No connection", other: "Couldn't send the rematch",
        }[passed.why]} />
        {refunded && (
          <div style={{ marginTop: "14px", display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "14px", background: "rgba(204,255,0,0.08)", border: "2px solid rgba(204,255,0,0.25)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"></path></svg>
            <span style={{ fontSize: "13px", fontWeight: "600" }}>{mine!.stake} USDT is back in your balance</span>
          </div>
        )}
        {wait > 0 && <span style={{ marginTop: "12px", textAlign: "center", fontSize: "12px", color: "#8B949E" }}>You can ask them again in {mmss(wait)}.</span>}
        <button style={{ ...BTN_P, marginTop: "16px" }} onClick={withLink}>CHALLENGE WITH A LINK</button>
        <button style={{ ...BTN_S, marginTop: "10px" }} onClick={() => { setPassed(null); go("App10"); }}>Find an opponent</button>
        <button style={{ marginTop: "6px", height: "36px", background: "none", border: "0", color: "#8B949E", fontSize: "13px", cursor: "pointer" }} onClick={() => setPassed(null)}>Close</button>
      </Sheet>
    );
  }
  return { tap, busy, label: canAfford ? null : "TOP UP TO REMATCH", sheet };
}

/** Rematch offers that reach a player who has gone back to the lobby. */
export function LobbyRematch() {
  const { incoming } = useGame();
  const offer = incoming[0];
  return offer ? <RematchOffer offer={offer} /> : null;
}
